import React, { useReducer, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import axios from 'axios';
import queryString from 'query-string';
import { ToastContainer } from 'react-toastify';

import SearchClient from '../common/SearchClient';
import ClientInfo from '../common/ClientInfo';
import Archive from '../common/Archive';
import Filter from '../common/Filter';
import filterReducer from '../../reducers/filter.reducer';
import FilterDispatch from '../../contexts/filterDispatch';
import Table from '../common/Table';
import filterActions from '../../actions/filter.actions';
import {
  ActionsButton,
  AgencyUser,
  Archived,
  BusinessName,
  getUserName,
} from '../client-page-common/CellRenderers';
import Format from '../../helpers/format';

import style from './ViewPoliciesPage.module.css';

const ViewPoliciesPage = React.memo(({ history }) => {
  const filterDefault = 'Next90Days';
  const archivedDefault = false;
  const unarchivedDefault = true;

  const [data, setData] = useState({
    clients: [],
    csrUsers: [],
    producerUsers: [],
    clientsTotal: -1,
    policiesCount: -1,
    totalPremium: -1,
    uaUrl: '/',
    pages: -1,
  });

  const options = [
    {
      id: 'BoundPolicies',
      value: 'BoundPolicies',
      text: 'All',
    },
    {
      id: 'Next120Days',
      value: 'Next120Days',
      text: 'Next 120 Days',
    },
    {
      id: filterDefault,
      value: filterDefault,
      text: 'Next 90 Days',
      defaultValue: true,
    },
    {
      id: 'Next60Days',
      value: 'Next60Days',
      text: 'Next 60 Days',
    },
    {
      id: 'Next30Days',
      value: 'Next30Days',
      text: 'Next 30 Days',
    },
    {
      id: 'RenewedLast30Days',
      value: 'RenewedLast30Days',
      text: 'Renewed Last 30 Days',
    },
  ];

  const getCurrentProducer = producerId => {
    return data.producerUsers.find(item => item.value === producerId);
  };

  const getCurrentCsr = csrId => {
    return data.csrUsers.find(item => item.value === csrId);
  };

  const columnSet = [
    {
      accessor: 'Id',
      show: false,
      filterable: false,
    },
    {
      accessor: 'Archived',
      Header: 'Archive',
      maxWidth: 60,
      sortable: false,
      Cell: cell => <Archived cell={cell} history={history} />,
    },
    {
      accessor: 'BusinessName',
      Header: 'Client Name',
      defaultContent: '',
      minWidth: 90,
      width: 'auto',
      maxWidth: 150,
      Cell: cell => <BusinessName cell={cell} uaUrl={data.uaUrl} />,
    },
    {
      accessor: 'ClientId',
      Header: 'Client ID',
    },
    {
      accessor: 'ClientCode',
      Header: 'Client Code',
      width: 150,
      Cell: cell => <span title={cell.value}>{cell.value}</span>,
    },
    {
      accessor: 'PrimaryContactPhone',
      Header: 'Phone',
      width: 130,
      Cell: cell => (
        <span title={Format.formatPhoneOrFax(cell.value)}>
          {Format.formatPhoneOrFax(cell.value)}
        </span>
      ),
    },
    {
      accessor: 'ProducerId',
      Header: 'Producer ID',
      show: false,
      filterable: false,
    },
    {
      accessor: 'Producer',
      Header: 'Producer',
      Cell: cell => {
        const clientSummary = cell.original;
        return (
          <AgencyUser
            className="col-fw-170px o-vis"
            clientId={clientSummary.Id}
            currentUser={[getCurrentProducer(clientSummary.ProducerId)]}
            users={data.producerUsers}
            csrAgencyId={clientSummary.CSRAgencyId}
            csrId={clientSummary.CSRId}
            width={180}
            producer
          />
        );
      },
      width: 180,
      style: { overflow: 'visible' },
    },
    {
      accessor: 'ProducerId',
      Header: 'Account Manager ID',
      show: false,
      filterable: false,
    },
    {
      accessor: 'csr',
      Header: 'Account Manager',
      Cell: cell => {
        const clientSummary = cell.original;
        return (
          <AgencyUser
            className="col-fw-170px o-vis"
            clientId={clientSummary.Id}
            currentUser={[getCurrentCsr(clientSummary.CSRId)]}
            users={data.csrUsers}
            producerAgencyId={clientSummary.ProducerAgencyId}
            producerId={clientSummary.ProducerId}
            producer={false}
            width={200}
          />
        );
      },
      width: 200,
      style: { overflow: 'visible' },
    },
    {
      accessor: 'EarliestExpirationDate',
      Header: 'Earliest Exp Date',
      width: 120,
      Cell: cell => Format.formatDate(cell.value),
    },
    {
      accessor: 'ActivePoliciesCount',
      Header: 'Policies',
      width: 70,
    },
    {
      accessor: 'AccountSize',
      Header: 'Acct. Size',
      width: 'auto',
      Cell: cell => Format.formatMoney(cell.value),
    },
    {
      accessor: 'actions',
      Header: '',
      sortable: false,
      Cell: cell => ActionsButton(cell.original),
      className: '',
      minWidth: 150,
    },
  ];

  const queryParameters = history.location.search;
  const params = new URLSearchParams(queryParameters);
  let pageState = {
    draw: params.get('draw') || 0,
    clientType: params.get('clientType') || filterDefault,
    page: +params.get('page') || 0,
    pageSize: +params.get('pageSize') || 10,
    archived:
      params.get('archived') && params.get('archived') !== null
        ? params.get('archived') === 'true'
        : false,
    unarchived:
      params.get('unarchived') && params.get('unarchived') !== null
        ? params.get('unarchived') === 'true'
        : true,
    q: params.get('q') || '',
    submitted: true,
    sortField: params.get('sortField') || '',
    sortDesc: params.get('sortDesc') || true,
    op: params.get('op') || '0',
  };

  const [state, dispatch] = useReducer(filterReducer, {
    filterChanged: false,
  });

  const { filterChanged } = state;

  const mapUsers = users => {
    return users
      ? users
          .map(u => {
            return {
              value: u.Id,
              label: getUserName(u),
            };
          })
          .filter(u => {
            return !u.label.toLowerCase().startsWith('disable');
          })
      : [];
  };

  const retrieveClients = () => {
    const handleSuccess = response => {
      const { data: responseData } = response;

      const newPagesNumber = Math.ceil(
        responseData.recordsTotal / pageState.pageSize,
      );
      if (responseData) {
        setData({
          csrUsers: mapUsers(responseData.CsrUsers),
          producerUsers: mapUsers(responseData.ProducerUsers),
          clients: responseData.data,
          clientsTotal: responseData.recordsTotal,
          policiesCount: responseData.extraData.PoliciesCount,
          totalPremium: responseData.extraData.TotalPremium,
          uaUrl: responseData.UaUrl,
          pages: newPagesNumber,
        });
      }
    };

    const handleError = error => {
      // eslint-disable-next-line no-console
      console.log(error);
    };

    const newParams = queryString.stringify(pageState);

    const url = `${process.env.REACT_APP_BASE_URL}agency/clients/hub?${newParams}`;

    axios
      .get(url)
      .then(handleSuccess)
      .catch(handleError);
  };

  useEffect(() => {
    if (filterChanged) {
      retrieveClients();
    }
  }, [state]);

  useEffect(() => {
    retrieveClients();

    document.title = 'HUB Portal: View Policies';
  }, []);

  const onTableStateChanged = changes => {
    pageState = { ...pageState, ...changes };
    history.push(
      `${history.location.pathname}?${queryString.stringify(pageState)}`,
    );

    dispatch({
      type: filterActions.FILTER_CHANGED_SUCCESS,
    });
  };

  return (
    <FilterDispatch.Provider value={dispatch}>
      <ToastContainer />
      <div className={style.policies}>
        <h1>Policies</h1>
        <div className={style.main}>
          <SearchClient history={history} />
          <ClientInfo
            premiums={data.totalPremium}
            clients={data.clientsTotal}
            policies={data.policiesCount}
          />
          <Archive
            history={history}
            archivedDefault={archivedDefault}
            unarchivedDefault={unarchivedDefault}
          />
          <Filter
            options={options}
            history={history}
            defaultFilter={filterDefault}
          />
          <Table
            data={data.clients}
            page={pageState.page}
            pages={data.pages}
            pageSize={pageState.pageSize}
            collapseOnPageChange={false}
            entityNameText="clients"
            columns={columnSet}
            manual
            onPageChange={newValue => onTableStateChanged({ page: newValue })}
            onPageSizeChange={newValue =>
              onTableStateChanged({ pageSize: newValue })
            }
            onSortedChange={newValue =>
              onTableStateChanged({
                sortField: newValue[0] ? newValue[0].id : '',
                sortDesc: newValue[0] ? newValue[0].desc : true,
              })
            }
            style={{ width: '100%', marginTop: '20px' }}
            pageSizeOptions={[5, 10, 25, 50, 100]}
          />
        </div>
      </div>
    </FilterDispatch.Provider>
  );
});

ViewPoliciesPage.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  history: PropTypes.object.isRequired,
};

export default withRouter(ViewPoliciesPage);
